import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import { colors } from '../theme';
import { useTheme } from '../contexts/ThemeContext';

export interface LogoProps {
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /** Kept for API compatibility only — ignored. Existing callers pass
   * 'white' on screens with light/theme backgrounds (it used to map to the
   * same gold artwork), so the word always follows the theme's text color. */
  variant?: 'default' | 'white';
  /** Mark + "DISLIX" word (default) or the mark alone. */
  showWordmark?: boolean;
  style?: ViewStyle;
}

// Exact registered family name (src/utils/fonts.ts registers it for both
// native Font.loadAsync and web @font-face). typography.fontFamily.* is a
// CSS fallback list, which native platforms can't resolve — the word fell
// back to the system font there.
const WORDMARK_FONT = 'Tajawal-Bold';

// Tajawal's line box (same for Bold and Black) is ascent 643 / descent 357 (unitsPerEm 1000) with a
// cap height of 633, so all-caps "DISLIX" sits in the top of the box: the
// caps' visual centre is (643 - 633/2) / 1000 ≈ 0.327em from the top vs the
// box centre at 0.5em. Nudging the glyphs down by the difference centres the
// word on the mark instead of on Arabic-descender space it never uses.
const CAP_CENTER_OFFSET_EM = 0.5 - (643 - 633 / 2) / 1000;

const SIZES = {
  small: { mark: 32, font: 21 },
  medium: { mark: 48, font: 32 },
  large: { mark: 64, font: 43 },
  xlarge: { mark: 80, font: 54 },
};

/**
 * The DISLIX mark — same artwork the website renders in its header/footer
 * (sendifier_website/assets/identity/logo/dislix-mark.svg): three open arcs
 * around a solid core, drawn with react-native-svg from the source paths
 * (1024×1024 viewBox) so it stays crisp at every size on web and native —
 * followed by the "DISLIX" word in Tajawal Bold (the app's and website's
 * main typeface) in the theme's text color — gold lives in the mark only —
 * vertically centred on the mark.
 */
export const Logo: React.FC<LogoProps> = ({ size = 'medium', showWordmark = true, style }) => {
  // Subscribes to theme changes so the word repaints on light/dark toggle.
  useTheme();
  const { mark, font } = SIZES[size];
  const wordColor = colors.textDark as string;

  return (
    <View style={[styles.container, style]}>
      <Svg width={mark} height={mark} viewBox="0 0 1024 1024">
        <Path d="M 819.9 253.6 A 402 402 0 1 1 374.5 134.2" fill="none" stroke="#A66E1C" strokeWidth={34} strokeLinecap="round" />
        <Path d="M 751.0 311.5 A 312 312 0 1 1 405.3 218.8" fill="none" stroke="#DA942C" strokeWidth={44} strokeLinecap="round" />
        <Path d="M 682.1 369.3 A 222 222 0 1 1 436.1 303.4" fill="none" stroke="#F2C97E" strokeWidth={50} strokeLinecap="round" />
        <Circle cx={512} cy={512} r={106} fill="#DA942C" />
      </Svg>
      {showWordmark && (
        <Text
          style={[
            styles.wordmark,
            {
              fontSize: font,
              lineHeight: font,
              height: font,
              marginLeft: Math.round(mark * 0.22),
              letterSpacing: font * 0.02,
              color: wordColor,
              transform: [{ translateY: Math.round(font * CAP_CENTER_OFFSET_EM) }],
            },
          ]}
          numberOfLines={1}
          allowFontScaling={false}
        >
          DISLIX
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    // A brand lockup reads mark → word in every locale; without this,
    // dir="rtl" on web (Arabic) would mirror the row.
    direction: 'ltr',
  },
  wordmark: {
    fontFamily: WORDMARK_FONT,
    includeFontPadding: false,
  },
});
